<template>
    <Card>
        <FetchTable
            :handle="tableHandle"
            :url="url"
            :columns="columns"
            :has-checkbox="true"
            @update:selectedValues="selectedProductUuids = $event"
            @fetch:success="updateSelectedPriceTemplateByProductUuid"
        >
            <template #table-body>
                <tr>
                    <td></td>
                    <td>
                        <Button color="light" size="sm" class="btn-icon" @click="clearNewOrderProduct" v-if="newProduct">
                            <i class="icon-cross2"></i>
                        </Button>
                    </td>
                    <td>
                        <AlphaButton color="teal" size="sm" @click="addNewOrderProduct" :disabled="!newProduct" v-if="newProduct">
                            <i class="icon-plus3"></i>
                        </AlphaButton>
                    </td>
                    <td>
                        <AppAutoComplete
                            url="/catalog/product-items"
                            :model-value="newProduct?.uuid"
                            :selectedValue="newProduct?.name"
                            @update:model-value="selectNewOrderProduct"
                        >
                            <template #optionItem="{ option }">
                                <div class="d-flex align-items-center gap-2">
                                    <img :src="option.main_image_url" :style="{ width: '38px', minWidth: '38px', height: '38px', objectFit: 'cover' }" class="mr-2" />
                                    <div>
                                        <span class="long-string" :title="option.name">{{ option.name }}</span>
                                        <small class="long-string text-muted" v-html="getAdditionalProductInfo(option)" :title="getAdditionalProductInfo(option)"></small>
                                    </div>
                                </div>
                            </template>
                        </AppAutoComplete>
                    </td>
                    <td>
                        <div v-if="newProduct">
                            <AppTouchSpinInput
                                v-model="newProductAmount"
                                :min="1"
                            />
                        </div>
                    </td>
                    <td>
                        <div v-if="newProduct" style="text-align: right;">
                            <Input
                                v-model="newProductPrice"
                                @update:modelValue="newProductSelectedPriceTemplate = null"
                                type="number"
                            />
                            <AppDropdown
                                @update:model-value="selectNewProductPriceTemplate(newProduct, $event)"
                                :model-value="priceTemplateOption(newProduct, newProductSelectedPriceTemplate)"
                                :options="priceTemplateOptionsForProduct(newProduct)"
                                :disabled="!newProduct || priceTemplateOptionsForProduct(newProduct).length === 0"
                                option-value="display"
                                class="mt-1"
                            />
                        </div>
                    </td>
                    <td>
                        <div class="d-flex justify-content-end" v-if="newProduct">
                            {{ Price.factory(newProductAmount * newProductPrice).toCurrencyString('UAH') }}
                        </div>
                    </td>
                </tr>
            </template>
            <template #actionDropdownItems>
                <DropdownItem @click="$refs.EditProductPriceTemplateModal.show(selectedProductUuids)">{{ $t('buttons.select_price_template') }}</DropdownItem>
                <DropdownItem @click="$refs.DeleteProductsModal.show(selectedProductUuids)">{{ $t('buttons.delete') }}</DropdownItem>
                <DropdownItem @click="$refs.CreateProductPurchaseExpenseModal.show(selectedProductUuids)">{{ $t('createProductPurchaseExpenseModal.showButton') }}</DropdownItem>
            </template>
            <template #td-action="{ row }">
                <AlphaButton color="danger" size="sm" @click="deleteOrderProduct(row)">
                    <i class="icon-trash"></i>
                </AlphaButton>
            </template>
            <template #td-image="{ row }">
                <ImageZoomComponent
                    :url="row.product.main_image_url !== null ? row.product.main_image_url : PLACEHOLDER_IMAGE_URL"
                    :alt="row.product.name"
                    :default-width="50"
                    :default-height="50"
                />
            </template>
            <template #td-name="{ row }">
                <div>
                    <span class="long-string">
                        {{ row.product.name }}
                    </span>
                    <router-link :to="{ name: 'InternalProductPricesPage', params: { productUuid: row.product.uuid } }">{{ row.product.sku }}</router-link>
                </div>
            </template>
            <template #td-amount="{ row }">
                <AppTouchSpinInput
                    :model-value="row.amount"
                    @update:modelValue="(newAmount) => updateAmount(row, newAmount)"
                    :min="1"
                />
            </template>
            <template #td-price="{ row }">
                <div style="text-align: right;">
                    <Input
                        :model-value="row.price"
                        @update:modelValue="(newPrice) => { updatePrice(row, newPrice); dropPriceTemplateValue(row) }"
                        type="number"
                    />
                    <AppDropdown
                        @update:model-value="setPriceTemplateValue(row, $event)"
                        :model-value="priceTemplateOption(row.product, selectedPriceTemplateByProductUuid[row.uuid])"
                        :options="priceTemplateOptionsForProduct(row.product)"
                        :disabled="priceTemplateOptionsForProduct(row.product).length === 0"
                        option-value="display"
                        class="mt-1"
                    />
                </div>
            </template>
            <template #td-sum="{ row }">
                {{ Price.factory(row.amount * row.price).toCurrencyString('UAH') }}
            </template>
        </FetchTable>
    </Card>
    <EditProductPriceTemplateModal ref="EditProductPriceTemplateModal" @price-template:applied="fetchOrderProducts" />
    <DeleteProductsModal ref="DeleteProductsModal" :order="order" @products:deleted="fetchOrderProducts"/>
    <CreateProductPurchaseExpenseModal ref="CreateProductPurchaseExpenseModal" @update="() => {$emit('update'); fetchOrderProducts()}" />
</template>
<script>
import Card from "../../../../../components/new/CardComponent.vue";
import Button from "../../../../../components/new/button/AppButton.vue";
import DropdownItem from "../../../../components/btnDropdown/DropdownItem.vue";
import EditProductPriceTemplateModal from "../modals/EditProductPriceTemplateModal.vue";
import DeleteProductsModal from "../modals/DeleteProductsModal.vue";
import CreateProductPurchaseExpenseModal from "../modals/CreateProductPurchaseExpenseModal.vue";
import AppCheckbox from "../../../../../components/new/form/checkbox/AppCheckbox.vue";
import AppTouchSpinInput from "../../../../../components/new/form/AppTouchSpinInput.vue";
import Input from "../../../../../components/new/form/Input.vue";
import AppDropdown from "../../../../../components/new/form/select/AppDropdown.vue";
import AlphaButton from "../../../../../components/new/button/AlphaButton.vue";
import AppAutoComplete from "../../../../../components/new/form/autocomplete/AppAutoComplete.vue";
import ButtonDropdown from "../../../../components/btnDropdown/ButtonDropdown.vue";
import InputGroup from "../../../../../components/new/form/InputGroup.vue";
import AppSelect from "../../../../../components/new/form/select/AppSelect.vue";
import FetchTable from "../../../../../components/new/table/FetchTable.vue";
import { createTableHandle } from "../../../../../components/new/table/tableHandle";
import ImageZoomComponent from "../../../../../components/new/image/ImageZoomComponent.vue";

import Price from "../../../../../services/Price/Price";
import { httpClient } from "../../../../../services/HttpClient";
import MessageNotificator from "../../../../../services/MessageNotificator";
import { PLACEHOLDER_IMAGE_URL } from "../../../../../constants.ts";

export default {
    name: "ProductsCard",
    components: {
        Card, Button, EditProductPriceTemplateModal, DropdownItem, DeleteProductsModal,
        CreateProductPurchaseExpenseModal, AppCheckbox, AppTouchSpinInput, Input, AppDropdown, AlphaButton, AppAutoComplete, ButtonDropdown, InputGroup,
        AppSelect, FetchTable, ImageZoomComponent
    },
    emits: ['update'],
    props: {
        order: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            tableHandle: createTableHandle(),
            Price,
            PLACEHOLDER_IMAGE_URL,
            url: `/projects/${this.$route.params.projectUuid}/crm/orders/${this.order.uuid}/products`,
            columns: [
                { key: "action", name: this.$t('table.titles.action'), width: "34px", type: "string", order: false },
                { key: "image", name: this.$t('table.titles.image'), width: "80px", type: "string", order: false },
                { key: "name", name: this.$t('table.titles.name'), width: "300px", type: "string", order: false },
                { key: "amount", name: this.$t('table.titles.amount'), width: "150px", type: "string", order: false },
                { key: "price", name: this.$t('table.titles.price'), width: "150px", type: "string", order: false },
                { key: "sum", name: this.$t('table.titles.sum'), width: "150px", type: "number", order: false },
            ],
            selectedProductUuids: [],
            priceTemplates: [],
            selectedPriceTemplateByProductUuid: {},
            newProduct: null,
            newProductAmount: 1,
            newProductPrice: 1,
            newProductSelectedPriceTemplate: null,
        }
    },
    computed: {
        priceTemplatesUrl() {
            return `/projects/${this.$route.params.projectUuid}/pim/price-templates`;
        },
    },
    methods: {
        async fetchOrderProducts() {
            await this.tableHandle.fetchRows({
                extra_fields: ["product"],
            });

            this.updateSelectedPriceTemplateByProductUuid();
        },
        async fetchPriceTemplates() {
            const response = await httpClient.get(this.priceTemplatesUrl, { limit: 100 });
            this.priceTemplates = response.data.data;
            this.updateSelectedPriceTemplateByProductUuid();
        },
        updateSelectedPriceTemplateByProductUuid() {
            this.tableHandle.rows.forEach(orderProduct => {
                let productPriceTemplates = orderProduct.product.price_templates || {};
                for (const [priceTemplateUuid, price] of Object.entries(productPriceTemplates)) {
                    if (price === orderProduct.price) {
                        this.selectedPriceTemplateByProductUuid[orderProduct.uuid] = this.priceTemplates.find(priceTemplate => priceTemplate.uuid === priceTemplateUuid);
                        break;
                    }
                }
            });
        },
        async setPriceTemplateValue(orderProduct, priceTemplate) {
            this.selectedPriceTemplateByProductUuid[orderProduct.uuid] = priceTemplate;

            const response = await httpClient.get(`/catalog/product-items/${orderProduct.product.uuid}`);
            const productPriceTemplates = response.data.data.price_templates;
            const newPrice = productPriceTemplates?.[priceTemplate.uuid] ?? 0;

            await this.updatePrice(orderProduct, newPrice);
        },
        selectNewProductPriceTemplate(product, priceTemplate) {
            this.newProductSelectedPriceTemplate = priceTemplate;
            this.newProductPrice = product.price_templates?.[priceTemplate.uuid] || 1;
        },
        priceTemplateLabel(product, priceTemplate) {
            const price = product?.price_templates?.[priceTemplate.uuid];

            if (price === undefined || price === null) {
                return priceTemplate.name;
            }

            return `${Price.factory(price).toCurrencyString('UAH')} - ${priceTemplate.name}`;
        },
        priceTemplateOption(product, priceTemplate) {
            if (!priceTemplate) {
                return null;
            }

            return { ...priceTemplate, display: this.priceTemplateLabel(product, priceTemplate) };
        },
        priceTemplateOptionsForProduct(product) {
            return this.priceTemplates
                .filter(priceTemplate => priceTemplate.show_in_order)
                .map(priceTemplate => this.priceTemplateOption(product, priceTemplate));
        },
        dropPriceTemplateValue(orderProduct) {
            if (this.selectedPriceTemplateByProductUuid[orderProduct.uuid]) {
                delete this.selectedPriceTemplateByProductUuid[orderProduct.uuid];
            }
        },
        async updateAmount(orderProduct, newAmount) {
            await httpClient.patch(`${this.url}/${orderProduct.uuid}`, { amount: newAmount });
            await this.fetchOrderProducts();
            this.$emit('update');
        },
        async updatePrice(orderProduct, newPrice) {
            await httpClient.patch(`${this.url}/${orderProduct.uuid}`, { price: newPrice });
            await this.fetchOrderProducts();
            this.$emit('update');
        },
        async addNewOrderProduct() {
            if (!this.newProduct) return;

            await httpClient.post(this.url, {
                product_uuid: this.newProduct.uuid,
                amount: this.newProductAmount,
                price: this.newProductPrice
            });

            await this.fetchOrderProducts();
            this.clearNewOrderProduct();
            this.$emit('update');
        },
        async deleteOrderProduct(orderProduct) {
            new MessageNotificator().confirm(this.$t('alerts.confirm_delete', {
                value: this.$t('labels.product')
            }), async () => {
                await httpClient.delete(`${this.url}/${orderProduct.uuid}`);
                await this.fetchOrderProducts();
                this.$emit('update');
            });
        },
        clearNewOrderProduct() {
            this.newProduct = null;
            this.newProductAmount = 1;
            this.newProductPrice = 1;
            this.newProductSelectedPriceTemplate = null;
        },
        selectNewOrderProduct(product) {
            this.newProduct = product;
            if (product) {
                this.newProductAmount = 1;
                const priceTemplate = this.order.source?.price_template;
                if (priceTemplate) {
                    this.newProductPrice = product.price_templates?.[priceTemplate.uuid] || 1;
                    this.newProductSelectedPriceTemplate = priceTemplate;
                } else {
                    this.newProductPrice = product.price || 1;
                    this.newProductSelectedPriceTemplate = null;
                }
            } else {
                this.clearNewOrderProduct();
            }
        },
        getAdditionalProductInfo(product) {
            let info = [];
            
            info.push(product.sku);

            if (Object.keys(product.price_templates).length > 0) {
                for (const priceTemplate of this.priceTemplates) {
                    if (product.price_templates?.[priceTemplate.uuid]) {
                        info.push(`${priceTemplate.name}: ${Price.factory(product.price_templates[priceTemplate.uuid]).toCurrencyString('UAH')}`);
                    }
                }
            }

            return info.join(' | ');
        }
    },
    mounted() {
        this.fetchOrderProducts();
        this.fetchPriceTemplates();
    }
}
</script>
<style scoped>
.long-string {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    white-space: normal;
}
.search-block {
    display: flex;
    gap: 10px;
}
.card-body{
    display: flex;
    justify-content: space-between;
}
</style>